import { Link, router, useForm, usePage } from "@inertiajs/react"
import { BookOpen, Trash2Icon } from "lucide-react"
import { type ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { RegionBoundaryEditor } from "@/components/shared/RegionBoundaryEditor"
import { MapListView, type MapPin } from "@/components/shared/MapListView"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  AvailableUser,
  ContentHealth,
  RegionChild,
  RegionDetail,
  RegionOption,
  RegionPropertyItem,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface RegionMapEvent {
  id: number
  name: string
  starts_at: string | null
  lat: number
  lng: number
}

interface Props {
  region: RegionDetail
  all_regions: RegionOption[]
  available_users: AvailableUser[]
  map_pois: MapPin[]
  map_events: RegionMapEvent[]
  map_center: { lat: number; lng: number } | null
  content_health: ContentHealth
  content_health_urls: {
    stale: string
    pending: string
  }
  urls: {
    index: string
    show: string
    update: string
    destroy: string
    add_distributor: string
    remove_distributor: string
    pois: string
  }
}

function ContentHealthCard({
  health,
  urls,
}: {
  health: ContentHealth
  urls: { stale: string; pending: string }
}) {
  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Content health</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm">
          <span className="font-medium">{health.approved_count}</span>
          {" "}approved POIs
          {health.stale_count > 0 && (
            <>
              {" · "}
              <Link
                href={urls.stale}
                className="font-medium text-amber-600 hover:underline"
              >
                {health.stale_count} stale
              </Link>
            </>
          )}
          {health.pending_count > 0 && (
            <>
              {" · "}
              <Link
                href={urls.pending}
                className="font-medium text-orange-600 hover:underline"
              >
                {health.pending_count} pending moderation
              </Link>
            </>
          )}
        </p>
        {health.added_30d > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            +{health.added_30d} added in the last 30 days
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function GeneralForm({
  region,
  allRegions,
  updateUrl,
}: {
  region: RegionDetail
  allRegions: RegionOption[]
  updateUrl: string
}) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, patch, processing, isDirty } = useForm({
    name: region.name,
    slug: region.slug,
    parent_id: region.parent_id ? String(region.parent_id) : "",
    timezone: region.timezone ?? "",
    locale: region.locale ?? "",
    description: region.description ?? "",
    boundary_center_lat: region.boundary_center ? String(region.boundary_center[1]) : "",
    boundary_center_lng: region.boundary_center ? String(region.boundary_center[0]) : "",
    boundary_radius_km: region.boundary_radius_km ? String(region.boundary_radius_km) : "",
  })

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    patch(updateUrl)
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5 max-w-lg">
      <div className="grid gap-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={data.slug}
          onChange={(e) => setData("slug", e.target.value)}
          required
        />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="parent_id">Parent region</Label>
        <Select
          value={data.parent_id || "none"}
          onValueChange={(v) => setData("parent_id", v === "none" ? "" : v)}
        >
          <SelectTrigger id="parent_id">
            <SelectValue placeholder="None (root region)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None (root region)</SelectItem>
            {allRegions.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="timezone">Timezone</Label>
        <Input
          id="timezone"
          value={data.timezone}
          onChange={(e) => setData("timezone", e.target.value)}
          placeholder="e.g. Europe/Lisbon"
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="locale">Locale</Label>
        <Input
          id="locale"
          value={data.locale}
          onChange={(e) => setData("locale", e.target.value)}
          placeholder="e.g. pt"
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          rows={3}
        />
      </div>

      <div className="grid gap-1.5">
        <Label>Boundary</Label>
        <RegionBoundaryEditor
          center={
            data.boundary_center_lat && data.boundary_center_lng
              ? [parseFloat(data.boundary_center_lng), parseFloat(data.boundary_center_lat)]
              : null
          }
          radiusKm={data.boundary_radius_km ? parseFloat(data.boundary_radius_km) : null}
          boundaryGeoJSON={region.boundary_geojson}
          onChange={(center, radiusKm) => {
            setData({
              ...data,
              boundary_center_lat: String(center[1]),
              boundary_center_lng: String(center[0]),
              boundary_radius_km: String(radiusKm),
            })
          }}
        />
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={processing || !isDirty}>
          {processing ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  )
}

function DistributorsTab({
  region,
  availableUsers,
  addDistributorUrl,
  removeDistributorUrl,
}: {
  region: RegionDetail
  availableUsers: AvailableUser[]
  addDistributorUrl: string
  removeDistributorUrl: string
}) {
  const { data, setData, post, processing, reset } = useForm({ user_id: "" })
  const assignedIds = new Set(region.distributors.map((d) => d.id))
  const unassigned = availableUsers.filter((u) => !assignedIds.has(u.id))

  function handleAdd(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!data.user_id) return
    post(addDistributorUrl, {
      onSuccess: () => reset(),
    })
  }

  function handleRemove(userId: number) {
    router.delete(removeDistributorUrl, {
      data: { user_id: userId },
    })
  }

  return (
    <div className="grid gap-6 max-w-lg">
      <div>
        <h3 className="text-sm font-medium mb-3">
          Assigned distributors ({region.distributors_count})
        </h3>
        {region.distributors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No distributors assigned.</p>
        ) : (
          <div className="rounded-lg border divide-y">
            {region.distributors.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{d.name || d.email}</p>
                  <p className="text-xs text-muted-foreground">{d.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleRemove(d.id)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {unassigned.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="text-sm font-medium mb-3">Add distributor</h3>
            <form onSubmit={handleAdd} className="flex gap-2">
              <Select
                value={data.user_id}
                onValueChange={(v) => setData("user_id", v)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a user…" />
                </SelectTrigger>
                <SelectContent>
                  {unassigned.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name ? `${u.name} (${u.email})` : u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" disabled={processing || !data.user_id}>
                Add
              </Button>
            </form>
          </div>
        </>
      )}
    </div>
  )
}

function SubRegionsTab({ children }: { children: RegionChild[] }) {
  return (
    <div className="max-w-lg">
      <h3 className="text-sm font-medium mb-3">
        Sub-regions ({children.length})
      </h3>
      {children.length === 0 ? (
        <p className="text-sm text-muted-foreground">No sub-regions.</p>
      ) : (
        <div className="rounded-lg border divide-y">
          {children.map((child) => (
            <div key={child.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <Link
                  href={child.url}
                  className="text-sm font-medium hover:underline"
                >
                  {child.name}
                </Link>
                <p className="text-xs text-muted-foreground">/{child.slug}</p>
              </div>
              <Link href={child.url}>
                <Button variant="ghost" size="sm">
                  View
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PropertiesTab({ properties }: { properties: RegionPropertyItem[] }) {
  return (
    <div>
      <h3 className="text-sm font-medium mb-3">
        Properties ({properties.length})
      </h3>
      {properties.length === 0 ? (
        <p className="text-sm text-muted-foreground">No properties in this region.</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.slug}</TableCell>
                  <TableCell>
                    <Badge
                      variant={p.status === "active" ? "default" : "outline"}
                    >
                      {p.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default function RegionsShow({ region, all_regions, available_users, map_pois, map_events, map_center, content_health, content_health_urls, urls }: Props) {
  const { props, url } = usePage<SharedProps>()
  const isAdmin = props.user_context?.is_admin
  const canManagePois = isAdmin || (props.user_context?.managed_region_ids?.includes(region.id) ?? false)
  const currentTab = new URLSearchParams(url.split("?")[1] ?? "").get("tab") ?? "general"

  function handleTabChange(value: string) {
    router.get(urls.show, { tab: value }, { preserveState: true, replace: true })
  }

  function handleDelete() {
    if (!confirm(`Delete region "${region.name}"? This cannot be undone.`)) return
    router.delete(urls.destroy)
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Link href={urls.index} className="hover:underline">
              Regions
            </Link>
            <span>/</span>
            <span>{region.name}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{region.name}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
            <span>/{region.slug}</span>
            {region.parent_name && (
              <>
                <span>·</span>
                <span>Under {region.parent_name}</span>
              </>
            )}
            {region.has_boundary && (
              <>
                <span>·</span>
                <Badge variant="outline" className="text-xs">Boundary set</Badge>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline">
            <Link href={`${urls.show.split('?')[0]}/studio`}>
              <BookOpen className="mr-1.5 size-4" />
              Open Guide Studio
            </Link>
          </Button>
          {canManagePois && (
            <Button asChild size="sm" variant="outline">
              <Link href={urls.pois}>Manage POIs</Link>
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={handleDelete}
            >
              <Trash2Icon className="mr-1.5 size-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <ContentHealthCard health={content_health} urls={content_health_urls} />

      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="distributors">
            Distributors
            {region.distributors_count > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {region.distributors_count}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sub-regions">
            Sub-regions
            {region.children.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {region.children.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="properties">
            Properties
            {region.properties_count > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {region.properties_count}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pois">
            POIs
            {map_pois.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {map_pois.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="events">
            Events
            {map_events.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {map_events.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="general">
            <GeneralForm region={region} allRegions={all_regions} updateUrl={urls.update} />
          </TabsContent>

          <TabsContent value="distributors">
            <DistributorsTab
              region={region}
              availableUsers={available_users}
              addDistributorUrl={urls.add_distributor}
              removeDistributorUrl={urls.remove_distributor}
            />
          </TabsContent>

          <TabsContent value="sub-regions">
            <SubRegionsTab children={region.children} />
          </TabsContent>

          <TabsContent value="properties">
            <PropertiesTab properties={region.properties} />
          </TabsContent>

          <TabsContent value="pois">
            {map_pois.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No approved POIs with map coordinates in this region.
              </p>
            ) : (
              <MapListView pins={map_pois} center={map_center}>
                <div className="divide-y">
                  {map_pois.map((poi) => (
                    <div key={poi.id} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{poi.name}</p>
                        {poi.category && (
                          <p className="text-xs text-muted-foreground">{poi.category}</p>
                        )}
                      </div>
                      {poi.status && (
                        <Badge variant="outline" className="ml-3 shrink-0 text-xs capitalize">
                          {poi.status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </MapListView>
            )}
          </TabsContent>

          <TabsContent value="events">
            {map_events.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No upcoming public events with map coordinates in this region.
              </p>
            ) : (
              <MapListView
                pins={map_events.map((e) => ({ id: e.id, name: e.name, lat: e.lat, lng: e.lng }))}
                center={map_center}
              >
                <div className="divide-y">
                  {map_events.map((event) => (
                    <div key={event.id} className="px-4 py-3">
                      <p className="truncate text-sm font-medium">{event.name}</p>
                      {event.starts_at && (
                        <p className="text-xs text-muted-foreground">
                          {new Date(event.starts_at).toLocaleDateString(undefined, {
                            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </MapListView>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

RegionsShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
